// P1-011: Main Hub Layout — core hub screen per GDD SS4.1.2
import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { ResourceBar } from './ResourceBar';
import { NavBar } from './NavBar';
import type { NavTab } from './NavBar';
import { ServerRackPanel } from './ServerRackPanel';
import { GachaPanel } from './GachaPanel';
import { CollectionPanel } from './CollectionPanel';
import { ProjectPanel } from './ProjectPanel';
import { SettingsPanel } from './SettingsPanel';
import { useAgentStore } from '../stores/agentStore';
import { useHardwareStore } from '../stores/hardwareStore';
import { useProjectStore } from '@/shared/stores/projectStore';
import { usePlayerStore } from '@/shared/stores/playerStore';
import { BgmPlayer } from './BgmPlayer';
import { SpriteAnimator } from './SpriteAnimator';
import { useUIEventStore } from '@/shared/stores/uiEventStore';
import './phase1.css';

interface MainHubLayoutProps {
  renderContractGame?: (projectId: string, onComplete: () => void, onExit: () => void) => ReactNode;
  renderBattleGame?: (projectId: string, onComplete: () => void, onExit: () => void) => ReactNode;
  renderMaintenanceGame?: (projectId: string, onComplete: () => void, onExit: () => void) => ReactNode;
}

export function MainHubLayout({ renderContractGame, renderBattleGame, renderMaintenanceGame }: MainHubLayoutProps) {
  const [activeTab, setActiveTab] = useState<NavTab | null>(null);
  const [contractProjectId, setContractProjectId] = useState<string | null>(null);
  const [battleProjectId, setBattleProjectId] = useState<string | null>(null);
  const [maintenanceProjectId, setMaintenanceProjectId] = useState<string | null>(null);
  const formation = useAgentStore((s) => s.formation);
  const getTotalCompute = useHardwareStore((s) => s.getTotalCompute);
  const leader = formation[0] ?? null;

  const startContract = useCallback((projectId: string) => {
    setContractProjectId(projectId);
  }, []);

  const exitContract = useCallback(() => {
    setContractProjectId(null);
  }, []);

  const startBattle = useCallback((projectId: string) => {
    setBattleProjectId(projectId);
  }, []);

  const exitBattle = useCallback(() => {
    setBattleProjectId(null);
  }, []);

  const startMaintenance = useCallback((projectId: string) => {
    setMaintenanceProjectId(projectId);
  }, []);

  const exitMaintenance = useCallback(() => {
    setMaintenanceProjectId(null);
  }, []);


  /** Debug: skip the current embedded phase with mock data */
  const handleDebugSkip = useCallback(() => {
    const store = useProjectStore.getState();
    if (contractProjectId) {
      const project = store.projects[contractProjectId];
      if (project && !project.contractOutput) {
        store.completeContract(contractProjectId, {
          tracksFinal: { D: 2, S: 1, L: -1, B: 0 },
          contractTags: ['debug-skip'],
          success: true,
          score: 80,
          clauseOutcomes: [
            { clauseId: 'CL01', clauseText: 'Debug clause', result: 'success', threshold: 150, tagsAdded: [], trackChanges: {} },
          ],
        }, project.projectName || `Project-${contractProjectId.slice(0, 6)}`);
      }
      setContractProjectId(null);
    } else if (battleProjectId) {
      const project = store.projects[battleProjectId];
      if (project && !project.battleResult) {
        store.completeBattle(battleProjectId, {
          battleOutcome: 'VICTORY',
          battleStats: { turnsUsed: 5, turnsTotal: 10, totalDamageDealt: 999, maxCombo: 3, critCount: 2, overloadEvents: 0, skillsTriggered: 1 },
        });
      }
      setBattleProjectId(null);
    } else if (maintenanceProjectId) {
      const project = store.projects[maintenanceProjectId];
      if (project && !project.maintenanceResult) {
        store.completeMaintenance(maintenanceProjectId, {
          outcome: 'VICTORY',
          towersBuilt: 5,
          enemiesKilled: 20,
          wavesCompleted: 7,
          totalWaves: 7,
          baseHealthRemaining: 800,
          moneyEarned: 0,
        });
      }
      setMaintenanceProjectId(null);
    }
  }, [contractProjectId, battleProjectId, maintenanceProjectId]);

  // Handle tab change: toggle panel on/off + trigger portrait animation
  const [portraitTrigger, setPortraitTrigger] = useState(0);
  const handleTabChange = (tab: NavTab) => {
    setActiveTab((prev) => (prev === tab ? null : tab));
    setPortraitTrigger((n) => n + 1);
  };

  // Cross-phase event: switch to card-play-reaction sprite when playing a hand
  const playHandTrigger = useUIEventStore((s) => s.playHandTrigger);
  const [reactionAnim, setReactionAnim] = useState(false);
  useEffect(() => {
    if (playHandTrigger === 0) return;
    setReactionAnim(true);
  }, [playHandTrigger]);

  /** Render the active panel */
  const renderPanel = () => {
    switch (activeTab) {
      case 'hardware':
        return <ServerRackPanel />;
      case 'summon':
        return <GachaPanel />;
      case 'collection':
        return <CollectionPanel />;
      case 'projects':
        return <ProjectPanel onStartContract={startContract} onStartBattle={startBattle} onStartMaintenance={startMaintenance} />;
      case 'settings':
        return <SettingsPanel />;
      default:
        return null;
    }
  };

  const panelContent = renderPanel();

  return (
    <div className="main-hub">
      {/* Leader portrait — sprite-sheet idle / attack animation */}
      <SpriteAnimator
        src={reactionAnim
          ? '/assets/characters/character_main/attach-spritesheet.png'
          : '/assets/characters/character_main/idle1-spritesheet.png'}
        frameWidth={512}
        frameHeight={512}
        columns={12}
        totalFrames={145}
        fps={24}
        trigger={reactionAnim ? playHandTrigger : portraitTrigger}
        onComplete={reactionAnim ? () => setReactionAnim(false) : undefined}
        className="main-hub__leader-portrait"
      />
      {/* Invisible hover zone over the character */}
      <div className="main-hub__leader-hover" />
      {/* Leader info panel — shown on hover */}
      {leader && (
        <div className="main-hub__leader-stats">
          <div className="main-hub__leader-stats-name">{leader.name}</div>
          <div className="main-hub__leader-stats-role">
            LV.{leader.level} · {leader.type} · {'★'.repeat(leader.rarity)}
          </div>
          <div className="main-hub__leader-stats-divider" />
          <div className="main-hub__leader-stat">ATK {leader.attack}</div>
          <div className="main-hub__leader-stat">DEF {leader.defense}</div>
          <div className="main-hub__leader-stat">COMPUTE {getTotalCompute()}</div>
          <div className="main-hub__leader-stats-divider" />
          <div className="main-hub__leader-stats-skill">{leader.activeSkill.name}</div>
          <div className="main-hub__leader-stats-desc">{leader.activeSkill.description}</div>
        </div>
      )}

      {/* Main content — full width */}
      <div className="main-hub__content">
        <div className="main-hub__center">
          {/* Background image */}
          <div className="main-hub__background" />

          {/* Top bar: title + currency */}
          <div className="main-hub__top-bar">
            <span className="main-hub__top-bar-title">SILICON VALLEY: AI</span>
            <ResourceBar />
          </div>

          {/* Panel area: opens based on NavBar selection */}
          <div className="main-hub__panel-area">
            {contractProjectId && renderContractGame
              ? renderContractGame(contractProjectId, exitContract, exitContract)
              : battleProjectId && renderBattleGame
                ? renderBattleGame(battleProjectId, exitBattle, exitBattle)
                : maintenanceProjectId && renderMaintenanceGame
                  ? renderMaintenanceGame(maintenanceProjectId, exitMaintenance, exitMaintenance)
                  : panelContent}
          </div>
        </div>
      </div>

      {/* Bottom: NavBar */}
      <NavBar activeTab={activeTab} onTabChange={handleTabChange} />
      <BgmPlayer />

      {/* Debug tools */}
      {import.meta.env.VITE_DEBUG === 'true' && (
        <div className="fixed bottom-3 left-3 z-[200] flex flex-col gap-1.5 font-mono">
          {(contractProjectId || battleProjectId || maintenanceProjectId) && (
            <button
              className="py-1.5 px-3 bg-red-600/80 hover:bg-red-600 border-none rounded text-white text-[11px] cursor-pointer backdrop-blur-sm shadow-lg transition-colors"
              onClick={handleDebugSkip}
              title="Debug: skip current phase"
            >
              DEBUG SKIP {contractProjectId ? 'CONTRACT' : battleProjectId ? 'BATTLE' : 'MAINTENANCE'} &raquo;
            </button>
          )}
          <div className="flex gap-1.5">
            <button
              className="py-1 px-2 bg-amber-600/80 hover:bg-amber-600 border-none rounded text-white text-[11px] cursor-pointer backdrop-blur-sm shadow-lg transition-colors"
              onClick={() => usePlayerStore.getState().addFlop(1000)}
              title="Debug: +1000 FLOP"
            >
              +1000 ₣
            </button>
            <button
              className="py-1 px-2 bg-purple-600/80 hover:bg-purple-600 border-none rounded text-white text-[11px] cursor-pointer backdrop-blur-sm shadow-lg transition-colors"
              onClick={() => usePlayerStore.getState().addAGI(100)}
              title="Debug: +100 AGI"
            >
              +100 ◈
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
